class PaymentInformationService {
  #cashQoreAxiosInstance;

  constructor(cashQoreAxiosInstance) {
    if (!cashQoreAxiosInstance) {
      throw new Error('Axios instance is required');
    }
    this.#cashQoreAxiosInstance = cashQoreAxiosInstance;
    
  }


  /**
   * Gets the list of paginated payment summaries.
   * 
   * @param {Object} params
   * @param {number} params.limit The number of rows per page.
   * @param {number} params.offset The page number.
   * @param {('DateCreated'|'CashQoreTransactionId'|'CompanyIdentifier'|'DispatchId'|'Total')} params.orderBy The column header to order by.
   * @param {('asc'|'desc')} params.order The sort direction.
   * @returns {Promise<{
   *  count: number,
   *  data: Array<{
   *    Address: string,
   *    CashQoreTransactionId: string,
   *    City: string,
   *    CompanyIdentifier: string,
   *    DateCreated: string,
   *    DispatchDetailsId: number,
   *    DispatchId: string,
   *    FirstName: string,
   *    LastName: string,
   *    PostalCode: string,
   *    ServiceKey: string,
   *    State: string,
   *    StateProvidence: string,
   *    Subtotal: number,
   *    Tax: number,
   *    UserDispatchSessionId: string
   *  }>
   * }>}
   */
  getPaymentSummaries({
    limit = 25,
    offset = 0,
    orderBy = 'DateCreated',
    order = 'desc',
    search = null,
    startDate = null,
    endDate = null,
  }) {
    return this.#cashQoreAxiosInstance.get('/getPaymentSummaries', {
      params: {
        limit,
        offset,
        orderBy,
        order,
        search,
        startDate,
        endDate,
      },
    });
  }

   /**
   * Gets the retail payment by the transaction id.
   * 
   * @param {number} cashQoreTransactionId The primary identifier for the retail payment.
   * @returns {Promise<{
   *  data: { 
   *    data: {
   *     cashQore: Array<Object>,
   *     dispatchQore: {
   *       CashQoreTransactionId: string,
   *       CompanyIdentifier: string,
   *       DateCreated: string,
   *       DispatchDetailsId: number,
   *       DropOffAddress: string,
   *       DropOffCity: string,
   *       DropOffDistance: string,
   *       DropOffLocationName: string,
   *       DropOffPostalCode: string,
   *       DropOffStateProvidence: string,
   *       FirstName: string,
   *       LastName: string,
   *       PickUpAddress: string,
   *       PickUpCity: string,
   *       PickUpPostalCode: string,
   *       PickUpStateProvidence: string,
   *       ServiceKey: string,
   *       Situation: string,
   *       State: string,
   *       Subtotal: number,
   *       Tax: number,
   *       Total: number,
   *       UserDispatchSessionId: string
   *     }
   *   }
   *  }
   * }>} A promise that resolves with the response data of the list of retail payment purchase orders and the unpaginated total.
   */
  getPaymentDetail(cashQoreTransactionId) {
    return this.#cashQoreAxiosInstance.get('/getPaymentDetail', {
      params: {
        cashQoreTransactionId,
      },
    });
  }

  /**
   * Gets the retail payment by the transaction id.
   * 
   * @param {number} transactionId The primary identifier for the retail payment.
   * @returns {Promise<{
   *  data: { 
   *    data: {
   *     PoNumber: string,
   *     RetailPaymentId: string,
   *     DateCreated: string,
   *     Caller: {
   *        FirstName: string,
   *        LastName: string,
   *        Email: string
   *     },
   *     StripePaymentId: string,
   *     StripeInvoiceId: string,
   *     StripeDisputeId: string,
   *     DisputeDate: string,
   *     PaymentDetail:{
   *        SubTotal: number,
   *        Tax: number,
   *        Total: number,
   *       Currency: string
   *     },
   *     Refunds: Array<{
   *        RetailPaymentRefundId: string,
   *        RetailPaymentId: string,
   *        StripeRefundId: string,
   *        RefundAmount: number,
   *        RefundReasonId: number,
   *        RefundReason: string,
   *        RefundUser: string,
   *        RefundNote: string,
   *        IsFullRefund: boolean,
   *        TransactionId: string
   *      }>
   *     }
   *   }
   *  }
   * }>} A promise that resolves with the response data of the list of retail payment purchase orders and the unpaginated total.
   */
  getRetailPaymentDetails(transactionId) {
    return this.#cashQoreAxiosInstance.get('/getPurchaseOrderRetailPaymentDetails', {
      params: {
        transactionId,
      },
    });
  }

  /**
   * Gets the paginated list of retail payment purchase orders based on the request's param.
   * 
   * @returns {Promise<{
   *  data: { 
   *    retailPaymentPurchaseOrders: Array<{
   *      aboveBenefit: number,
   *      adjustDt: string,
   *      apprvDt: string,
   *      callDate: string,
   *      callStatus: string,
   *      csr: string,
   *      dispatchId: string,
   *      firstName: string,
   *      goa: boolean,
   *      insertedDate: string,
   *      lastName: string,
   *      method: string,
   *      modifiedDate: string,
   *      override: number,
   *      poAmount: number,
   *      poId: number,
   *      poStatus: string,
   *      producerCode: string,
   *      service: string,
   *      state: string,
   *      sup: number,
   *      towId: number
   *    }>,
   *    total: number
   *  }
   * }>} A promise that resolves with the response data of the list of retail payment purchase orders and the unpaginated total.
   */
  getRetailPaymentPurchaseOrders(params) {
    return this.#cashQoreAxiosInstance.get('/getRetailPaymentPurchaseOrders', { params });
  }

  /**
   * Gets retail payment purchase orders by dispatch id.
   * 
   * @param {number} dispatchId 
   * @returns {Promise<{
   *  data: { 
   *    retailPaymentPurchaseOrders: Array<{
   *      aboveBenefit: number,
   *      adjustDt: string,
   *      apprvDt: string,
   *      callDate: string,
   *      callStatus: string,
   *      csr: string,
   *      dispatchId: string,
   *      firstName: string,
   *      goa: boolean,
   *      insertedDate: string,
   *      lastName: string,
   *      method: string,
   *      modifiedDate: string,
   *      override: number,
   *      poAmount: number,
   *      poId: number,
   *      poStatus: string,
   *      producerCode: string,
   *      service: string,
   *      state: string,
   *      sup: number,
   *      towId: number
   *    }>
   *  }
   * }>} A promise that resolves with the response data of the list of retail payment purchase orders. 
   */
  getRetailPaymentPurchaseOrdersByDispatchId(dispatchId) {
    return this.#cashQoreAxiosInstance.get(`/getRetailPaymentPurchaseOrdersByDispatchId/${dispatchId}`);
  }

  /**
   * Gets refund details by the transaction id.
   * 
   * @param {number} tansactionId The primary identifier for the retail payment.
   * @returns {Promise<{
   *   data: {
   *     data: Array<{
   *       RetailPaymentRefundId: string,
   *       RetailPaymentId: string,
   *       StripeRefundId: string,
   *       RefundDate: string,
   *       RefundAmount: number,
   *       RefundUser: string,
   *       RefundNote: string,  
   *       IsFullRefund: boolean,
   *       TransactionId: string
   *     }>
   *   }
   * }>} A promise that resolves with the response data of the list of retail payment purchase orders and the unpaginated total.
   */
  getRefundsByTransactionId(transactionId) {
    return this.#cashQoreAxiosInstance.get('/getPurchaseOrderRefunds', {
      params: {
        transactionId,
      },
    });
  }
}

export default PaymentInformationService;